import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getResponseStatus } from '#/service/driver-management/utils/apiError.ts';
import {
  deleteAccount,
  getAccountById,
  getAccountResourceOwnerLinks,
  getAllAccounts,
  type GetAllAccountsParams,
} from '../endpoints/accountInfo.ts';

const ACCOUNT_QUERY_NAMESPACE = 'account';

const useAccountListData = (params: GetAllAccountsParams = {}) =>
  useQuery({
    queryKey: [ACCOUNT_QUERY_NAMESPACE, 'list', params],
    queryFn: () => getAllAccounts(params),
  });

const useAccountByIdData = (id: string) =>
  useQuery({
    queryKey: [ACCOUNT_QUERY_NAMESPACE, 'byId', id],
    queryFn: () => getAccountById(id),
  });

const useAccountResourceOwnerLinks = (accountId: string) =>
  useQuery({
    queryKey: [ACCOUNT_QUERY_NAMESPACE, 'resourceOwnerLinks', accountId],
    queryFn: () => getAccountResourceOwnerLinks(accountId),
  });

const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      try {
        await deleteAccount(id, reason);
      } catch (error) {
        if (getResponseStatus(error) === 404) return;
        throw error;
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [ACCOUNT_QUERY_NAMESPACE, 'list'],
      }),
  });
};

export {
  ACCOUNT_QUERY_NAMESPACE,
  useAccountListData,
  useAccountByIdData,
  useAccountResourceOwnerLinks,
  useDeleteAccount,
};
